// Deleting a team (RFC-1371 §7.2, RFC-1372 C7).
//
// ── A known unmet requirement, stated rather than hidden ───────────────────
//
// RFC-1372 C7 says the confirmation "must state the forfeited amount
// explicitly before the destructive action, not after". This dialog CANNOT do
// that: there is no `GET /teams/{id}/credits`, because the team-scoped wallet
// is RFC-1372 and has not shipped. So the warning says the balance is destroyed
// and does not name a figure.
//
// The alternative — showing a number we do not have — is the one thing worse
// than omitting it, and a plausible-looking zero would be actively misleading
// on a team that has credits. When the credits endpoint lands, fetch it here
// and put the amount in `deleteForfeitWarning`; the copy key already reads as a
// sentence that wants a number. Tracked in the report to the team lead.
//
// ── Blocks come from the server, after the attempt ─────────────────────────
//
// There is no delete-preview endpoint either, so "can I delete this" is
// answered by trying. The two refusals worth distinct copy are
// `team_not_empty` — which splits live and TRASHED counts, because
// `projects.owner_team_id` is a real FK with no ON DELETE and a soft-deleted
// project still blocks the delete, making an apparently empty team refuse for
// no visible reason — and `personal_team_immutable` (D10), which is the harder
// invariant: `uq_teams_personal` allows one personal team per account and
// nothing re-provisions a missing one, so deleting it would leave the account
// with no owner scope, no wallet and nowhere to create a project.
//
// The name must be typed to confirm, following DeleteAccountModal. Reserved for
// actions with no undo, and this one qualifies twice: the team row cascades,
// and the balance goes with it.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TeamsRequestError, teamNotEmptyCounts, type Team } from '../../api/teams'
import { useDeleteTeam } from '../../hooks/useTeams'
import { cn } from '../../lib/utils'
import {
  BackButton,
  DestructiveButton,
  ErrorText,
  SecondaryButton,
  WarningPanel,
} from './teamsChrome'
import { resultOf, trackInteraction } from '../../analytics/interactionAnalytics'
import { countBucket } from '../../analytics/buckets'

export default function DeleteTeamView({
  team,
  onDone,
  onCancel,
}: {
  team: Team
  onDone: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation('settings')
  const deleteTeam = useDeleteTeam()
  const [typedName, setTypedName] = useState('')
  const [blocked, setBlocked] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Trimmed, case-sensitive. A team name is user-chosen text the user is
  // looking at; accepting a near-match defeats the point of typing it.
  const nameMatches = typedName.trim() === team.name
  const canSubmit = nameMatches && !deleteTeam.isPending

  const handleDelete = () => {
    setError(null)
    setBlocked(null)
    const trackDelete = (ok: boolean) => trackInteraction('teams_team_deleted', {
      team_id: team.id, member_count_bucket: countBucket(team.memberCount), result: resultOf(ok),
    })
    deleteTeam.mutate(team.id, {
      onSuccess: () => { trackDelete(true); onDone() },
      onError: (err) => {
        trackDelete(false)
        const counts = teamNotEmptyCounts(err)
        if (counts !== null) {
          setBlocked(
            counts.trashed > 0 && counts.live === 0
              ? t('settingsModal.teams.deleteBlockedTrashedOnly', { count: counts.trashed })
              : t('settingsModal.teams.deleteBlockedProjects', {
                  live: counts.live,
                  trashed: counts.trashed,
                }),
          )
          return
        }
        if (!(err instanceof TeamsRequestError)) {
          setError(t('settingsModal.teams.deleteFailed'))
          return
        }
        if (err.code === 'personal_team_immutable') {
          setBlocked(t('settingsModal.teams.deleteBlockedPersonal'))
          return
        }
        if (err.code === 'team_forbidden') {
          setBlocked(t('settingsModal.teams.deleteBlockedNotOwner'))
          return
        }
        setError(err.message_ ?? t('settingsModal.teams.deleteFailed'))
      },
    })
  }

  return (
    <div className="flex flex-col gap-4" data-testid="teams-delete-view">
      <BackButton
        testId="teams-delete-back"
        label={t('settingsModal.teams.back')}
        onClick={onCancel}
      />

      <h4 className="text-lg font-semibold text-white">
        {t('settingsModal.teams.deleteTitle', { name: team.name })}
      </h4>

      <WarningPanel testId="teams-delete-forfeit-warning">
        <p>{t('settingsModal.teams.deleteForfeitWarning')}</p>
        <p className="mt-1.5">{t('settingsModal.teams.deleteIrreversible')}</p>
      </WarningPanel>

      {blocked !== null && (
        <WarningPanel testId="teams-delete-blocked">{blocked}</WarningPanel>
      )}

      <label className="flex flex-col gap-1.5 text-sm text-white/70">
        {t('settingsModal.teams.deleteTypeName', { name: team.name })}
        <input
          type="text"
          data-testid="teams-delete-name-input"
          value={typedName}
          onChange={(event) => setTypedName(event.target.value)}
          autoComplete="off"
          className={cn(
            'h-9 rounded-lg border border-white/15 bg-black/30 px-3 text-sm text-white',
            'placeholder:text-white/30',
            'focus:outline-none focus:ring-2 focus:ring-white/30',
          )}
        />
      </label>

      <div className="flex justify-end gap-2">
        <SecondaryButton testId="teams-delete-cancel" onClick={onCancel}>
          {t('settingsModal.teams.cancel')}
        </SecondaryButton>
        <DestructiveButton
          testId="teams-delete-submit"
          disabled={!canSubmit}
          onClick={handleDelete}
        >
          {t('settingsModal.teams.deleteTeam')}
        </DestructiveButton>
      </div>

      {error !== null && <ErrorText testId="teams-delete-error">{error}</ErrorText>}
    </div>
  )
}
